import { useState, useEffect } from 'react'
import { getProfile, saveProfile, fileToBase64, validateImageFile } from '../utils/profileManager'
import { useAuth } from '../contexts/AuthContext'

export const useProfileManager = () => {
  const { user, isAuthenticated, getCurrentUser } = useAuth()
  const [profile, setProfile] = useState(getProfile(user))
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Update profile when user data changes
  useEffect(() => {
    if (user) {
      setProfile(getProfile(user))
    }
  }, [user])

  const clearMessages = () => {
    setError('')
    setSuccess('')
  }

  const showSuccess = (message) => {
    setSuccess(message)
    setTimeout(() => setSuccess(''), 3000)
  }

  const showError = (message) => {
    setError(message)
  }

  const handleFileSelect = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    clearMessages()

    // Validate file
    const validation = validateImageFile(file)
    if (!validation.valid) {
      showError(validation.error)
      return
    }

    setIsUploading(true)

    try {
      // Convert to base64
      const base64 = await fileToBase64(file)

      // Update profile
      const updatedProfile = {
        ...profile,
        profilePicture: base64,
        profileType: 'picture'
      }

      // Save to localStorage and server
      const saved = await saveProfile(
        updatedProfile,
        isAuthenticated,
        isAuthenticated ? getCurrentUser : null
      )

      if (saved) {
        setProfile(updatedProfile)
        showSuccess('Profile picture updated successfully!')
      } else {
        showError('Failed to save profile picture')
      }
    } catch (err) {
      showError('Failed to process image')
      if (process.env.NODE_ENV === 'development') console.error('Upload error:', err)
    } finally {
      setIsUploading(false)
    }
  }

  const handleIconSelect = async (iconId) => {
    const updatedProfile = {
      ...profile,
      selectedIcon: iconId,
      profileType: 'icon'
    }

    const saved = await saveProfile(
      updatedProfile,
      isAuthenticated,
      isAuthenticated ? getCurrentUser : null
    )

    if (saved) {
      setProfile(updatedProfile)
      showSuccess('Icon updated successfully!')
    } else {
      showError('Failed to save icon selection')
    }
  }

  const handleRemovePicture = async () => {
    const updatedProfile = {
      ...profile,
      profilePicture: null,
      profileType: 'icon'
    }

    const saved = await saveProfile(
      updatedProfile,
      isAuthenticated,
      isAuthenticated ? getCurrentUser : null
    )

    if (saved) {
      setProfile(updatedProfile)
      showSuccess('Profile picture removed')
    } else {
      showError('Failed to remove profile picture')
    }
  }

  const refreshProfile = async () => {
    if (isAuthenticated && getCurrentUser) {
      await getCurrentUser()
    }
  }

  return {
    profile,
    isUploading,
    error,
    success,
    handleFileSelect,
    handleIconSelect,
    handleRemovePicture,
    refreshProfile
  }
}